import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './IndustryBase.scss';
import Header from 'modules/Shared/components/Header/Header';
import Footer from 'modules/Shared/components/Footer/Footer';
import FlightChecker from 'modules/MainModule/components/FlightChecker/FlightChecker';
import Contact from 'modules/MainModule/components/Contact/Contact';
import { colorBackgroundBrightness } from 'data/common.data';
import { Helmet, HelmetProvider } from 'react-helmet-async';

interface IndustryFeatureModel {
    key: string;
    text: string;
    header: string;
    logo: string;
    classname: string;
}
interface IndustryTypeModel {
    key: string;
    header: string;
    text: string;
    image: string;
    imageText: string;
    url: string;
}
const industryFeatureCards: IndustryFeatureModel[] = [
    { key: 'industry-feature-1', text: 'AirLegit\'s services are purpose-built to integrate with your existing infrastructure.', header: 'Built for Business', logo: '/images/icons/ALEG-Icon-Industries_BuiltBusiness.svg', classname: '' },
    { key: 'industry-feature-2', text: 'No infrastructure team? No problem. Our solutions operate as a turnkey solution.', header: 'Fully Managed', logo: '/images/icons/ALEG-Icon-Industries_FullyManaged.svg', classname: 'small-screen-padding-top' },
    { key: 'industry-feature-3', text: 'Our technology is tempered by a customer-centric sensibility. AirLegit\'s tools are easy on end-users.', header: 'Customer-centric', logo: '/images/icons/ALEG-Icon-Industries_Customer.svg', classname: 'small-screen-padding-top' },
    { key: 'industry-feature-4', text: 'Our platform works as well on the back end as it does for your clients.', header: 'Smartly Deployed', logo: '/images/icons/ALEG-Icon-Industries_Smartly.svg', classname: 'small-screen-padding-top large-screen-padding-top' },
    { key: 'industry-feature-5', text: 'AirLegit\'s platform is designed for scale using only proven technology that stays operational.', header: 'Robust', logo: '/images/icons/ALEG-Icon-Industries_Robust.svg', classname: 'small-screen-padding-top large-screen-padding-top' },
    { key: 'industry-feature-6', text: 'Launch day isn\'t our first rodeo. Our support team makes your traveler support rock-solid and reliable.', header: 'Tested', logo: '/images/icons/ALEG-Icon-Industries_Tested.svg', classname: 'small-screen-padding-top large-screen-padding-top' }
];
const industryTypeCards: IndustryTypeModel[] = [
    { key: 'industry-type-1', header: 'Solutions for Booking Companies', text: 'Boost customer loyalty while reducing customer costs with proactive, seamless flight disruption remediation.', image: '/images/others/ALEG-Image-Industry-Body_1.jpg', imageText: 'ONLINE TRAVEL AGENCIES', url: '/industries/ota' },
    { key: 'industry-type-2', header: '', text: '', image: '/images/others/ALEG-Image-Industry-Body_3.jpg', imageText: 'BUSINESS TRAVEL MANAGERS', url: '/industries/tmc' },
    { key: 'industry-type-3', header: 'Solutions for Carriers', text: 'Give your passengers confidence with complete travel awareness - throughout their itinerary.', image: '/images/others/ALEG-Image-Industry-Body_2.jpg', imageText: 'CARRIER SOLUTIONS', url: '/industries/airlines' },
    { key: 'industry-type-4', header: 'Solutions for Tech', text: 'AirLegit\'s stack supplements adjacent tech offerings for insurance and travel.', image: '/images/others/ALEG-Image-Industry-Body_1.jpg', imageText: 'TECH PROVIDERS', url: '/industries/travel-tech' }
];

const IndustryBase = (props: any) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    });

    const navigate = useNavigate();
    const gotolink = (link: string) => {
        navigate(link);
    }
    const cardItems = industryFeatureCards.map((card: IndustryFeatureModel) => {
        let classData = 'col-md-4 industry-feature-card';
        if (card.classname) classData = `${classData} ${card.classname}`;
        return (
            <div key={card.key} className={classData}>
                <div className='industry-feature-card-wrapper'>
                    <div className='logo-wrapper'>
                        <img src={card.logo} alt={card.header} />
                    </div>
                    <div className='content-wrapper'>
                        <div className='content-header text-l2 text-med'>
                            {card.header}
                        </div>
                        <div className='content-text'>
                            {card.text}
                        </div>
                    </div>
                </div>
            </div>
        );
    });
    const industryTypeImage = (card: IndustryTypeModel) => {
        const styleData = { backgroundImage: `linear-gradient(${colorBackgroundBrightness}, ${colorBackgroundBrightness}), url(${card.image})` };
        return (
            <div className='industry-type-image' style={styleData}>
                <div className='content-header text-med text-l2'>
                    {card.imageText}
                </div>
                <div className='btn-wrapper'>
                    <button onClick={() => gotolink(card.url)} className='btn-primary'>
                        Learn more &#8594;
                    </button>
                </div>
            </div>
        );
    }
    const industryTypeText = (card: IndustryTypeModel) => {
        const styleData = card.key === 'industry-type-4' ? { marginBottom: '0px' } : {};
        return (
            <div className='industry-type-text' style={styleData}>
                <div className='content-header'>
                    {card.header}
                </div>
                <div className='content-separator'></div>
                <div className='content-text'>
                    {card.text}
                </div>
            </div>
        );
    }
    const headerBg: string = '/images/backgrounds/ALEG-Image-Industry_Header.jpg';
    const styleData = { backgroundImage: `linear-gradient(${colorBackgroundBrightness}, ${colorBackgroundBrightness}), url(${headerBg})` };
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>AirLegit - Tools Travelers Appreciate</title>
                    <meta name='description' content='Facilitate the kind of customer delight that keeps them coming back.' />
                </Helmet>
            </HelmetProvider>
            <Header />
            <div className='page-min-height industry-data-container'>
                <div className='header-container container-side-padding container-content-padding' style={styleData}>
                    <div className='container-small-width'>
                        <div className='container no-padding'>
                            <div className='row'>
                                <div className='col-md-6'>
                                    <h1>
                                        Tools Travelers<br />
                                        Appreciate
                                    </h1>
                                    <p className=''>
                                        How our clients deliver the remedy to travelers' top concern: 'What if my flight is canceled or delayed?'
                                    </p>
                                </div>
                                <div className='col-md-6'>
                                    <div className='logo-wrapper'>
                                        <img src='/images/others/ALEG-Image-Header_TabletPhone.png' alt='tools travelers appreciate' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='text-container container-side-padding container-content-padding'>
                    <div className='container-small-width'>
                        <div className='container no-padding'>
                            <div className='row'>
                                <div className='col-12 no-padding text-med'>
                                    When your customers receive notification of a potential disruption and updates on its remediation, you have facilitated the kind of customer delight that keeps them coming back.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='feature-container container-side-padding container-content-padding'>
                    <div className='container-large-width'>
                        <div className='container no-padding'>
                            <div className='row'>
                                {cardItems}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='industry-type-container container-side-padding container-content-padding'>
                    <div className='container-large-width'>
                        <div className='row'>
                            <div className='col-md-6 order-1'>
                                {industryTypeImage(industryTypeCards[0])}
                                {industryTypeImage(industryTypeCards[1])}
                            </div>
                            <div className='col-md-6 order-1'>
                                {industryTypeText(industryTypeCards[0])}
                            </div>
                            <div className='col-md-6 order-3 order-md-2'>
                                {industryTypeText(industryTypeCards[2])}
                            </div>
                            <div className='col-md-6 order-2 order-md-3'>
                                {industryTypeImage(industryTypeCards[2])}
                            </div>
                            <div className='col-md-6 order-4'>
                                {industryTypeImage(industryTypeCards[3])}
                            </div>
                            <div className='col-md-6 order-4'>
                                {industryTypeText(industryTypeCards[3])}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flight-checker-container container-side-padding container-content-padding'>
                    <div className='container-small-width'>
                        <div className='container no-padding'>
                            <div className='row'>
                                <div className='col-12 no-padding'>
                                    <FlightChecker />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Contact />
            <Footer />
        </>
    );
};

export default IndustryBase;